// An invitation link /invite/<token> (GIOTHUB-186): what it is for, then
// sign-in with the invited e-mail, then accept.
import { useEffect, useState } from "preact/hooks";
import { api, ApiError } from "../lib/api";
import { signedIn } from "../lib/auth";
import { setActiveOrg } from "../lib/org";
import { fmtDateTime, setLang, t, useLang, type Lang } from "../i18n";
import type { Me } from "../lib/types";
import { Centered } from "../App";
import { Login } from "./Login";
import { roleName } from "./Shell";
import "./auth.css";

interface Preview {
  organisation: { id: string; name: string };
  email: string;
  role: string;
  lang: Lang;
  inviter: string;
  expires_at: string;
  state: "open" | "accepted" | "expired" | "revoked";
}

export function Invite({ token }: { token: string }) {
  useLang();
  const [p, setP] = useState<Preview | null>(null);
  const [auth, setAuth] = useState<boolean | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    fetch(`/api/v1/invitations/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new ApiError(r.status, r.status === 404 ? t("invite.unknown") : r.statusText);
        const pv = (await r.json()) as Preview;
        setP(pv);
        // the invitation's language, unless the person chose one already
        try {
          if (!localStorage.getItem("gtb.lang") && pv.lang) setLang(pv.lang);
        } catch {
          /* ignore */
        }
      })
      .catch((e) => setErr(e.message));
    signedIn().then(setAuth);
  }, [token]);
  useEffect(() => {
    if (auth) api<Me>("GET", "/me").then(setMe).catch(() => {});
  }, [auth]);
  if (err) return <Centered>{err}</Centered>;
  if (!p || auth === null) return <Centered>{t("common.loading")}</Centered>;
  const lead = t("invite.lead", { org: p.organisation.name, role: roleName(p.role) });
  if (p.state !== "open") {
    return <Centered>{t(p.state === "accepted" ? "invite.accepted" : p.state === "expired" ? "invite.expired" : "invite.revoked")}</Centered>;
  }
  if (!auth) return <Login returnTo={`/invite/${token}`} lead={`${lead} ${t("invite.signin", { email: p.email })}`} />;
  const accept = async () => {
    setBusy(true);
    try {
      const r = await api<{ organisation: { id: string } }>("POST", `/invitations/${encodeURIComponent(token)}/accept`);
      setActiveOrg(r.organisation.id);
      location.href = "/";
    } catch (x) {
      setErr((x as Error).message);
      setBusy(false);
    }
  };
  const wrong = me && me.email && me.email.toLowerCase() !== p.email;
  return (
    <div class="auth h-full">
      <aside class="side" style="grid-column: 1 / -1; border-right: 0">
        <div class="box">
          <h1 style="margin:0;font:600 26px/1.15 var(--f-disp)">{t("invite.title", { org: p.organisation.name })}</h1>
          <p class="lead">{lead}</p>
          <p class="lead">{p.inviter ? t("invite.from", { who: p.inviter }) + " " : ""}{t("invite.until", { date: fmtDateTime(p.expires_at) })}</p>
          {wrong && <p class="msg" role="alert">{t("invite.wrong", { email: me!.email, invited: p.email })}</p>}
          {me && me.email_verified === false && <p class="msg" role="alert">{t("invite.unverified")}</p>}
          <button class="abtn pri" type="button" disabled={busy} onClick={accept}>{t("invite.accept")}</button>
        </div>
      </aside>
    </div>
  );
}
